<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useEventMedia } from '@/composables/useEventMedia'

const { t } = useI18n()
const route = useRoute()

const eventSlug = route.params['eventSlug'] as string

const {
  hero,
  loading,
  fieldErrors,
  uploadProgress,
  fetchHero,
  uploadHero,
  deleteHero,
  stopPolling,
} = useEventMedia(eventSlug)

const inputRef = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)
const isUploading = ref(false)
const clientError = ref<string | null>(null)

const isProcessing = computed(
  () => hero.value?.status === 'pending' || hero.value?.status === 'processing',
)
const isReady = computed(() => hero.value?.status === 'ready')
const isFailed = computed(() => hero.value?.status === 'failed')

const sizeInMb = computed(() => {
  if (!hero.value) return '0'
  return (hero.value.bytes / (1024 * 1024)).toFixed(1)
})

const thumbUrl = computed(() => {
  if (!hero.value) return null
  return hero.value.urls.thumb?.webp ?? hero.value.urls.thumb?.jpeg ?? null
})

onMounted(() => void fetchHero())
onUnmounted(() => stopPolling())

function openFilePicker(): void {
  inputRef.value?.click()
}

function onDragOver(): void {
  isDragging.value = true
}

function onDragLeave(): void {
  isDragging.value = false
}

async function onDrop(event: DragEvent): Promise<void> {
  isDragging.value = false
  const file = event.dataTransfer?.files[0]
  if (file) {
    await handleFile(file)
  }
}

async function onFileChange(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (file) {
    await handleFile(file)
  }
  // Reset so the same file can be re-selected after an error
  input.value = ''
}

async function handleFile(file: File): Promise<void> {
  clientError.value = null
  fieldErrors.value = {}

  // MIME validation
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
    clientError.value = t('events.media.hero.error_mime')
    return
  }

  // Size validation (10 MB)
  if (file.size > 10 * 1024 * 1024) {
    clientError.value = t('events.media.hero.error_size')
    return
  }

  // Dimension validation
  const dimensionsOk = await checkDimensions(file)
  if (!dimensionsOk) {
    clientError.value = t('events.media.hero.error_dimensions')
    return
  }

  isUploading.value = true
  try {
    await uploadHero(file)
  } catch {
    // fieldErrors is populated by the composable for 422 responses
  } finally {
    isUploading.value = false
  }
}

async function checkDimensions(file: File): Promise<boolean> {
  const url = URL.createObjectURL(file)
  try {
    const img = new Image()
    img.src = url
    await img.decode()
    return img.naturalWidth >= 1600 && img.naturalHeight >= 900
  } catch {
    return false
  } finally {
    URL.revokeObjectURL(url)
  }
}

async function handleDelete(): Promise<void> {
  await deleteHero()
}

function handleRetry(): void {
  clientError.value = null
  openFilePicker()
}

const visibleError = computed<string | null>(() => {
  if (clientError.value) return clientError.value
  const imageErrors = fieldErrors.value['image']
  if (imageErrors && imageErrors.length > 0) return imageErrors[0] ?? null
  return null
})
</script>

<template>
  <div class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 sm:p-8">
    <div class="mb-6">
      <h2 class="text-lg font-semibold text-white">
        {{ t('events.media.hero.heading') }}
      </h2>
      <p class="text-sm text-slate-400 mt-1">
        {{ t('events.media.hero.description') }}
      </p>
    </div>

    <!-- Loading state -->
    <div v-if="loading && !hero" class="flex items-center justify-center py-12">
      <div class="h-6 w-6 animate-spin rounded-full border-2 border-slate-600 border-t-emerald-400" />
    </div>

    <!-- Uploading state -->
    <div v-else-if="isUploading" class="space-y-4">
      <div class="border-2 border-dashed border-slate-600 rounded-xl p-12 flex flex-col items-center gap-4">
        <div class="h-8 w-8 animate-spin rounded-full border-2 border-slate-600 border-t-emerald-400" />
        <div class="w-full max-w-xs">
          <div class="h-2 bg-slate-700 rounded-full overflow-hidden">
            <div
              class="h-full bg-emerald-500 rounded-full transition-all duration-300"
              :style="{ width: `${uploadProgress}%` }"
            />
          </div>
          <p class="text-xs text-slate-400 text-center mt-2">{{ uploadProgress }}%</p>
        </div>
      </div>
    </div>

    <!-- Processing state -->
    <div v-else-if="isProcessing" class="space-y-4">
      <div class="relative rounded-xl overflow-hidden border border-slate-600">
        <img
          v-if="thumbUrl"
          :src="thumbUrl"
          class="w-full h-48 object-cover opacity-50"
          alt=""
        />
        <div
          v-else
          class="w-full h-48 bg-slate-700"
        />
        <div class="absolute inset-0 flex items-center justify-center bg-slate-900/60">
          <div class="flex items-center gap-3">
            <div class="h-5 w-5 animate-spin rounded-full border-2 border-slate-400 border-t-white" />
            <span class="text-sm font-medium text-white animate-pulse">
              {{ t('events.media.hero.status_processing') }}
            </span>
          </div>
        </div>
      </div>
      <div class="flex gap-3">
        <button
          type="button"
          disabled
          class="px-4 py-2 rounded-lg text-sm font-medium bg-emerald-700 text-emerald-200 opacity-50 cursor-not-allowed"
        >
          {{ t('events.media.hero.btn_replace') }}
        </button>
        <button
          type="button"
          disabled
          class="px-4 py-2 rounded-lg text-sm font-medium text-slate-400 border border-slate-600 opacity-50 cursor-not-allowed"
        >
          {{ t('events.media.hero.btn_remove') }}
        </button>
      </div>
    </div>

    <!-- Ready state -->
    <div v-else-if="isReady && hero" class="space-y-4">
      <div class="rounded-xl overflow-hidden border border-slate-600">
        <picture>
          <source
            v-if="hero.urls.medium?.avif"
            type="image/avif"
            :srcset="hero.urls.medium.avif"
          />
          <source
            v-if="hero.urls.medium?.webp"
            type="image/webp"
            :srcset="hero.urls.medium.webp"
          />
          <img
            :src="hero.urls.medium?.jpeg"
            class="w-full object-cover max-h-64"
            alt=""
          />
        </picture>
      </div>
      <p class="text-xs text-slate-400">
        {{ hero.width }} × {{ hero.height }} — {{ sizeInMb }} MB
      </p>
      <div class="flex gap-3">
        <button
          type="button"
          class="px-4 py-2 rounded-lg text-sm font-medium bg-emerald-600 hover:bg-emerald-500 text-white transition-colors"
          @click="openFilePicker"
        >
          {{ t('events.media.hero.btn_replace') }}
        </button>
        <button
          type="button"
          class="px-4 py-2 rounded-lg text-sm font-medium text-slate-300 border border-slate-600 hover:border-slate-400 hover:text-white transition-colors"
          @click="handleDelete"
        >
          {{ t('events.media.hero.btn_remove') }}
        </button>
      </div>
    </div>

    <!-- Failed state -->
    <div v-else-if="isFailed && hero" class="space-y-4">
      <div class="border-2 border-red-500/60 rounded-xl p-6 bg-red-900/10">
        <p class="text-sm font-medium text-red-400 mb-1">
          {{ t('events.media.hero.status_failed') }}
        </p>
        <p v-if="hero.error" class="text-sm text-red-300">{{ hero.error }}</p>
      </div>
      <button
        type="button"
        class="px-4 py-2 rounded-lg text-sm font-medium bg-slate-700 hover:bg-slate-600 text-white transition-colors"
        @click="handleRetry"
      >
        {{ t('events.media.hero.btn_retry') }}
      </button>
    </div>

    <!-- Empty state (no hero) -->
    <div v-else class="space-y-3">
      <div
        class="border-2 border-dashed rounded-xl p-10 flex flex-col items-center gap-3 transition-colors cursor-pointer"
        :class="isDragging ? 'border-emerald-400 bg-emerald-900/10' : 'border-slate-600 hover:border-slate-500'"
        aria-live="polite"
        @dragover.prevent="onDragOver"
        @dragleave="onDragLeave"
        @drop.prevent="onDrop"
      >
        <label for="hero-file-input" class="flex flex-col items-center gap-3 cursor-pointer">
          <svg class="w-10 h-10 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1.5"
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span class="text-sm font-medium text-slate-300 text-center">
            {{ t('events.media.hero.dropzone_label') }}
          </span>
          <span class="text-xs text-slate-500 text-center">
            {{ t('events.media.hero.dropzone_hint') }}
          </span>
        </label>
      </div>

      <!-- Validation error -->
      <p v-if="visibleError" class="text-sm text-red-400">
        {{ visibleError }}
      </p>
    </div>

    <!--
      Hidden file input, kept outside the state-driven v-if/v-else-if/v-else chain above so it
      stays mounted (and `inputRef` stays non-null) in every state. "Replace" (ready state) and
      "Try again" (failed state) open it programmatically via openFilePicker()/inputRef.value.click();
      the empty-state dropzone label opens it via the `for`/`id` association instead of wrapping it.
    -->
    <input
      id="hero-file-input"
      ref="inputRef"
      type="file"
      accept="image/jpeg,image/png,image/webp"
      class="sr-only"
      @change="onFileChange"
    />
  </div>
</template>
